import React, { Suspense, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import {
	Button,
	Card,
	CardContent,
	CardHeader,
	Typography,
	useTheme,
} from "@mui/material";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";

import { Translate } from "@/components/Application/Translate";
import { tokens } from "@/components/Application/theme";
import HistoryTable from "@/components/Archive/Components/History/HistoryTable";
import { ArchiveProcedureSearch } from "@/components/Archive/Pages/ArchiveProcedure/Components/ArchiveProcedureSearch";

import ComponentError from "@/helpers/components/Errors/Components";
import Spinner from "@/helpers/components/Spinner";

import { initialDate } from "@/service/ApplicationQueries";

type Props = {
	initialRowsPerPage?: number;
};

const HistoryContainer = (props: Props) => {
	const { initialRowsPerPage = 5 } = props;
	const [startDate, setStartDate] = useState(initialDate(-14));
	const [endDate, setEndDate] = useState(initialDate());
	const [selectedUser, setSelectedUser] = useState(0);
	const [showFilters, setShowFilters] = useState<boolean>(false);

	const theme = useTheme();
	const colors = tokens(theme.palette.mode);

	const options = {
		StartDate: startDate ? dayjs(startDate).toDate() : undefined,
		EndDate: endDate
			? dayjs(endDate).startOf("day").add(1, "day").toDate()
			: undefined,
		SelectedUser: selectedUser ?? 0,
	};

	return (
		<>
			<Typography
				variant="h4"
				component="h1"
				sx={{ mt: 1, mb: 2, fontWeight: "550" }}
			>
				<Translate String="Archive.History.Header">History</Translate>
			</Typography>
			<Card
				elevation={4}
				sx={{
					borderColor: colors.grey[200],
				}}
			>
				<CardHeader
					subheader={`From ${dayjs(options.StartDate).format("DD/MM/YYYY")} to ${dayjs(options.EndDate).format("DD/MM/YYYY")}`}
					action={
						<Button
							onClick={() => setShowFilters(!showFilters)}
							variant="outlined"
							sx={{
								color: colors.blueAccent[300],
								borderColor: colors.blueAccent[300],
							}}
						>
							<Translate String="Archive.History.Filters">Filters</Translate>
						</Button>
					}
				/>
				<CardContent>
					<QueryErrorResetBoundary>
						{({ reset }) => (
							<Sentry.ErrorBoundary
								onReset={reset}
								fallback={({ error, resetError }) => (
									<ComponentError message={error.message} reset={resetError} />
								)}
							>
								<Suspense fallback={<Spinner text={"Loading history"} />}>
									{showFilters ? (
										<ArchiveProcedureSearch
											startDate={startDate}
											endDate={endDate}
											selectedUser={selectedUser}
											SetStartDate={setStartDate}
											SetEndDate={setEndDate}
											SetSelectedUser={setSelectedUser}
										/>
									) : null}
									<HistoryTable
										initialRowsPerPage={initialRowsPerPage}
										options={options}
									/>
								</Suspense>
							</Sentry.ErrorBoundary>
						)}
					</QueryErrorResetBoundary>
				</CardContent>
			</Card>
		</>
	);
};

export default HistoryContainer;
